// Talks to the site's own API, on the same address as the page. A short session is opened first and renewed when it ends.
export function createApi(mount, extraHeaders = () => ({})) {
  let session = (() => {
    try {
      const stored = sessionStorage.getItem('vs:session');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.token && parsed?.until > Date.now() + 10000) return parsed;
      }
    } catch {}
    return null;
  })();

  const cache = new Map();
  const CACHE_TTL_MS = 60000; // 60s in-memory cache for instant page-to-page navigation

  async function open() {
    const res = await fetch(mount + 'v1/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body?.token) {
      throw new Error(body?.message || ('Session error ' + res.status));
    }
    session = { token: body.token, until: Date.now() + (body.expires_in - 30) * 1000 };
    try {
      sessionStorage.setItem('vs:session', JSON.stringify(session));
    } catch {}
  }

  async function request(method, path, body, retried = false) {
    if (!session || session.until < Date.now()) await open();
    const res = await fetch(mount + path, {
      method,
      headers: {
        Authorization: 'Bearer ' + session.token,
        ...extraHeaders(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if ((res.status === 401 || res.status === 403) && !retried) {
      session = null;
      try { sessionStorage.removeItem('vs:session'); } catch {}
      return request(method, path, body, true);
    }
    const data = await res.json().catch(() => null);
    if (!res.ok || data?.error) {
      if (data?.error === 'invalid_session' && !retried) {
        session = null;
        try { sessionStorage.removeItem('vs:session'); } catch {}
        return request(method, path, body, true);
      }
      const err = new Error(data?.message ?? 'Request failed');
      err.status = res.status;
      err.code = data?.error;
      throw err;
    }
    return data;
  }

  const query = (params) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    const s = q.toString();
    return s ? '?' + s : '';
  };

  async function getCached(path, params = {}) {
    const fullPath = path + query(params);
    const now = Date.now();
    const hit = cache.get(fullPath);
    if (hit && hit.expires > now && hit.data) return hit.data;
    if (hit && hit.promise) return hit.promise;

    const promise = request('GET', fullPath).then((data) => {
      cache.set(fullPath, { data, expires: Date.now() + CACHE_TTL_MS });
      return data;
    }).catch((err) => {
      cache.delete(fullPath);
      throw err;
    });

    cache.set(fullPath, { promise, expires: now + 15000 });
    return promise;
  }

  return {
    get: (path, params = {}) => getCached(path, params),
    getFresh: (path, params = {}) => request('GET', path + query(params)),
    post: (path, body) => request('POST', path, body),
    clearCache: () => cache.clear(),
    /** An address on this site, for a link or a video source */
    url: (path) => new URL(path.replace(/^\//, ''), new URL(mount, location.origin)).toString(),
  };
}
